import type { ErrorLike } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { invalidateAccountActivity, invalidateBatches } from './useBatchTrackingQueries';
import { Kitchen } from '../libs';
import {
  BATCH_TRACKING_CREATE_BATCH_MUTATION,
  BATCH_TRACKING_DELETE_BATCH_MUTATION,
  BATCH_TRACKING_SCAN_LABEL_MUTATION,
  BATCH_TRACKING_UPDATE_BATCH_MUTATION
} from '../libs/graphQL/batchTracking';
import { newLabelId } from '../libs/utils/newLabelId';
import { Batch, ManualBatchInput, ScanLabelInput, ScanResult } from '../views/BatchTracking/types';

/** Sends the label and the kitchen only: the server takes the caller from the identity. Only the account activity is read again; the batch lists are not. */
export interface UseScanLabelResult {
  scanLabel: (args: Omit<ScanLabelInput, 'kitchen'>) => Promise<ScanResult>;
  loading: boolean;
  error: ErrorLike | undefined;
}

export const useScanLabel = (kitchen: Kitchen): UseScanLabelResult => {
  const client = useApolloClient();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending, error } = useMutation({
    onSuccess: () => invalidateAccountActivity(queryClient, kitchen),
    mutationFn: async (args: Omit<ScanLabelInput, 'kitchen'>): Promise<ScanResult> => {
      const { data } = await client.mutate<{ scanLabel: ScanResult }>({
        mutation: BATCH_TRACKING_SCAN_LABEL_MUTATION,
        variables: { input: { ...args, kitchen } }
      });
      return data!.scanLabel;
    }
  });
  return { scanLabel: mutateAsync, loading: isPending, error: error ?? undefined };
};

const useBatchMutation = <TVariables, TData>(kitchen: Kitchen, send: (variables: TVariables) => Promise<TData>) => {
  const queryClient = useQueryClient();
  // Returned, so the call resolves once the lists on screen have read again.
  return useMutation({ mutationFn: send, onSuccess: () => invalidateBatches(queryClient, kitchen) });
};

export interface UseDeleteBatchResult {
  /** Deletes the batch and every batch made from it in the kitchen of the hook; rejects with the Apollo error (Unauthorized without the permission). */
  deleteBatch: (id: string) => Promise<void>;
  loading: boolean;
}

const useDeleteMutation = (kitchen: Kitchen) => {
  const client = useApolloClient();
  return useBatchMutation(kitchen, async (id: string): Promise<void> => {
    await client.mutate({ mutation: BATCH_TRACKING_DELETE_BATCH_MUTATION, variables: { kitchen, id } });
  });
};

/** The right of EDIT_BATCH_TRACKING_VOID: the batches stay for trace, so they are read again and shown as deleted. */
export const useDeleteBatch = (kitchen: Kitchen): UseDeleteBatchResult => {
  const { mutateAsync, isPending } = useDeleteMutation(kitchen);
  return { deleteBatch: mutateAsync, loading: isPending };
};

export interface UseManualBatchResult {
  /** Each rejects with the Apollo error and resolves once the Home timeline has read the batches again. */
  create: (input: ManualBatchInput) => Promise<Batch>;
  update: (id: string, input: ManualBatchInput) => Promise<Batch>;
  remove: (id: string) => Promise<void>;
  loading: boolean;
}

/** Records, changes and deletes a batch by hand in the kitchen of the hook; the server takes the creator from the identity. */
export const useManualBatch = (kitchen: Kitchen): UseManualBatchResult => {
  const client = useApolloClient();
  const creating = useBatchMutation(kitchen, async (input: ManualBatchInput): Promise<Batch> => {
    const { data } = await client.mutate<{ createBatchV2: Batch }>({
      mutation: BATCH_TRACKING_CREATE_BATCH_MUTATION,
      variables: { input: { ...input, kitchen, labelId: newLabelId() } }
    });
    return data!.createBatchV2;
  });
  const updating = useBatchMutation(kitchen, async ({ id, input }: { id: string; input: ManualBatchInput }): Promise<Batch> => {
    const { data } = await client.mutate<{ updateBatchV2: Batch }>({
      mutation: BATCH_TRACKING_UPDATE_BATCH_MUTATION,
      variables: { input: { ...input, id, kitchen } }
    });
    return data!.updateBatchV2;
  });
  const removing = useDeleteMutation(kitchen);

  return {
    create: creating.mutateAsync,
    update: (id, input) => updating.mutateAsync({ id, input }),
    remove: removing.mutateAsync,
    loading: creating.isPending || updating.isPending || removing.isPending
  };
};
